import type React from 'react';
import { useEffect, useRef } from 'react';
import type { AudioAnalyserService } from './services/audio-analyser.service';
import { SuperbarDark } from '@caddie/ui-kit/components/icons';
import { useSuperbarTheme } from './hooks/useSuperbarTheme';
import { useAnalytics } from './hooks/useAnalytics';
import { useGetStoreInfo } from './hooks/useGetStoreInfo';
import { useGetProductId } from './hooks/useGetProductId';
import { AnalyticsEvent } from './constants/analyticsEvents';
import { appendSbRef } from './utils/append-sb-ref';
import { cn } from './utils/styles';

export type GlowState = 'idle' | 'speaking';

type GlowBackgroundProps = {
  state?: GlowState;
  /** 0..1 amplitude; when undefined it behaves like idle */
  level?: number;
  /** Audio analyser service to pull amplitude from without React re-renders */
  analyserService?: AudioAnalyserService | null;
  /** Corner radius if clipping is enabled */
  radius?: number;
  /** Clip the glow to a rounded rect */
  clipToRadius?: boolean;
  /** Left glow color */
  leftColor?: string;
  /** Right glow color */
  rightColor?: string;

  rightTransitionColor?: string;
  /** Overall opacity of the whole effect */
  opacity?: number;
  /** Canvas blur amount used by the glow */
  blur?: number;
  /** Kept for API compatibility, not applied to the arch canvas */
  backdropBlur?: number;
  poweredByColor?: string;
  logoColor?: string;
  className?: string;
  style?: React.CSSProperties;
  expanded?: boolean;
};

type ArchPoint = [number, number];

export const GlowBackground: React.FC<GlowBackgroundProps> = ({
  expanded = false,
  state = 'idle',
  level = 0,
  analyserService,
  radius = 24,
  clipToRadius = false,
  leftColor = '#4B2DFF',
  rightColor = '#cd8451',
  rightTransitionColor = '#FFB15C',
  opacity = 1,
  blur = 36,
  backdropBlur = 0,
  poweredByColor = '#FFF',
  logoColor = '#FFF',
  className,
  style,
}) => {
  const archCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const glowsCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const levelRef = useRef(level);
  const stateRef = useRef(state);
  const interpolationRef = useRef(state === 'speaking' ? 1 : 0);
  const lastTimeRef = useRef(performance.now());

  const expandedRef = useRef(expanded);
  const leftColorRef = useRef(leftColor);
  const rightColorRef = useRef(rightColor);
  const opacityRef = useRef(opacity);
  const blurRef = useRef(blur);
  const radiusRef = useRef(radius);
  const clipToRadiusRef = useRef(clipToRadius);
  const analyserRef = useRef(analyserService);
  const grainCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useSuperbarTheme();

  const { trackEvent } = useAnalytics();
  const { store } = useGetStoreInfo();
  useGetProductId(store?.config?.config);

  const storeConfig = store?.config?.config;
  const subscriptionName = store?.subscription?.name;
  const isFreePlan =
    !subscriptionName ||
    subscriptionName.toLowerCase() === 'free' ||
    subscriptionName.toLowerCase() === 'unknown' ||
    subscriptionName.toLowerCase() === 'unknown plan';

  const shouldHidePoweredBy = storeConfig?.hidePoweredBySuperbar || !isFreePlan;

  const SUPERBAR_URL =
    'http://www.superbar.ai/?utm_source=product&utm_medium=active-superbar';

  const handlePoweredBySuperbarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const urlWithRef = appendSbRef(SUPERBAR_URL);

    trackEvent(AnalyticsEvent.POWERED_BY_SUPERBAR_CLICK, {
      sb_ref_host: window.location.host,
      dest_url: urlWithRef,
      page_origin: window.location.origin,
    });

    window.open(urlWithRef, '_blank');
  };

  levelRef.current = level;
  stateRef.current = state;
  analyserRef.current = analyserService;
  expandedRef.current = expanded;
  leftColorRef.current = leftColor;
  rightColorRef.current = rightColor;
  opacityRef.current = opacity;
  blurRef.current = blur;
  radiusRef.current = radius;
  clipToRadiusRef.current = clipToRadius;

  useEffect(() => {
    const archCanvas = archCanvasRef.current;
    const glowsCanvas = glowsCanvasRef.current;
    if (!archCanvas || !glowsCanvas) return;

    const archCtx = archCanvas.getContext('2d');
    const glowsCtx = glowsCanvas.getContext('2d');
    if (!archCtx || !glowsCtx) return;

    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

    let totalWidth = window.innerWidth;
    let parentWidth = 0;
    let parentLeft = 0;

    const resize = () => {
      const parent = archCanvas.parentElement || archCanvas;
      const rect = parent.getBoundingClientRect();

      totalWidth = window.innerWidth;
      parentWidth = parent.clientWidth;
      parentLeft = rect.left;

      // Taller canvas gives the arch more room to blur without clipping.
      const canvasHeight = 560;

      [archCanvas, glowsCanvas].forEach((canvas) => {
        canvas.width = Math.floor(totalWidth * dpr);
        canvas.height = Math.floor(canvasHeight * dpr);

        canvas.style.width = `${totalWidth}px`;
        canvas.style.height = `${canvasHeight}px`;

        canvas.style.left = `${-parentLeft}px`;
        canvas.style.top = `${Math.round(-canvasHeight * 0.52)}px`;
      });
    };

    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(archCanvas.parentElement || archCanvas);
    window.addEventListener('resize', resize);

    lastTimeRef.current = performance.now();

    const getArchPoints = (
      barCenterY: number,
      height: number,
      peakBoost: number
    ): ArchPoint[] => {
      /**
       * Higher peak, close to the top of the Superbar.
       */
      const peakY = barCenterY / height - 0.22 - peakBoost;

      /**
       * Wide plateau design:
       * - peak stays high across the middle of the bar
       * - slopes start much farther out
       * - color reaches both sides before dropping
       */
      const plateauY = peakY;
      const shoulderY = peakY + 0.16;
      const valleyY = peakY + 0.46;
      const edgeY = peakY + 0.56;

      return [
        // Left wide base
        [0.0, edgeY],
        [0.04, edgeY],
        [0.08, valleyY + 0.02],
        [0.13, valleyY],
        [0.19, valleyY - 0.04],
        [0.25, valleyY - 0.1],

        // Long, soft left climb
        [0.31, shoulderY + 0.08],
        [0.36, shoulderY + 0.035],

        // Start of wider top plateau
        [0.4, plateauY + 0.04],
        [0.44, plateauY + 0.015],
        [0.48, plateauY],
        [0.52, plateauY],
        [0.56, plateauY + 0.015],
        [0.6, plateauY + 0.04],

        // Long, soft right descent
        [0.64, shoulderY + 0.035],
        [0.69, shoulderY + 0.08],

        // Right wide base
        [0.75, valleyY - 0.1],
        [0.81, valleyY - 0.04],
        [0.87, valleyY],
        [0.92, valleyY + 0.02],
        [0.96, edgeY],
        [1.0, edgeY],
      ];
    };

    const createArchPath = (
      ctx: CanvasRenderingContext2D,
      points: ArchPoint[],
      width: number,
      height: number,
      bottomY: number
    ) => {
      ctx.beginPath();
      ctx.moveTo(points[0][0] * width, points[0][1] * height);

      for (let i = 1; i < points.length - 1; i++) {
        const [cx, cy] = points[i];
        const [nx, ny] = points[i + 1];

        ctx.quadraticCurveTo(
          cx * width,
          cy * height,
          ((cx + nx) / 2) * width,
          ((cy + ny) / 2) * height
        );
      }

      const [lastX, lastY] = points[points.length - 1];

      ctx.lineTo(lastX * width, lastY * height);
      ctx.lineTo(width, bottomY);
      ctx.lineTo(0, bottomY);
      ctx.closePath();
    };

    const makeSoftShapeMask = (
      points: ArchPoint[],
      width: number,
      height: number,
      maskBlur: number,
      yOffset: number
    ) => {
      const mask = document.createElement('canvas');
      mask.width = width;
      mask.height = height;

      const maskCtx = mask.getContext('2d');
      if (!maskCtx) return null;

      maskCtx.clearRect(0, 0, width, height);

      maskCtx.save();
      maskCtx.filter = `blur(${maskBlur}px)`;
      maskCtx.translate(0, yOffset);
      createArchPath(maskCtx, points, width, height, height + 260 * dpr);
      maskCtx.fillStyle = '#fff';
      maskCtx.fill();
      maskCtx.restore();

      return mask;
    };

    const drawSoftMaskedGradient = (
      ctx: CanvasRenderingContext2D,
      points: ArchPoint[],
      width: number,
      height: number,
      gradient: CanvasGradient,
      options: {
        maskBlur: number;
        yOffset: number;
        alpha: number;
        topFadeStart: number;
        topFadeEnd: number;
      }
    ) => {
      const layer = document.createElement('canvas');
      layer.width = width;
      layer.height = height;

      const layerCtx = layer.getContext('2d');
      if (!layerCtx) return;

      layerCtx.clearRect(0, 0, width, height);

      layerCtx.save();
      layerCtx.globalAlpha = options.alpha;
      layerCtx.filter = `saturate(210%) contrast(120%) brightness(105%)`;
      layerCtx.fillStyle = gradient;
      layerCtx.fillRect(0, 0, width, height);
      layerCtx.restore();

      // Vertical fade makes the top dissolve instead of looking like a hard cut.
      const verticalFade = layerCtx.createLinearGradient(0, 0, 0, height);
      verticalFade.addColorStop(0, 'rgba(0,0,0,0)');
      verticalFade.addColorStop(options.topFadeStart, 'rgba(0,0,0,0)');
      verticalFade.addColorStop(options.topFadeEnd, 'rgba(0,0,0,1)');
      verticalFade.addColorStop(1, 'rgba(0,0,0,1)');

      layerCtx.save();
      layerCtx.globalCompositeOperation = 'destination-in';
      layerCtx.fillStyle = verticalFade;
      layerCtx.fillRect(0, 0, width, height);
      layerCtx.restore();

      const mask = makeSoftShapeMask(
        points,
        width,
        height,
        options.maskBlur,
        options.yOffset
      );

      if (!mask) return;

      layerCtx.save();
      layerCtx.globalCompositeOperation = 'destination-in';
      layerCtx.drawImage(mask, 0, 0);
      layerCtx.restore();

      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      ctx.drawImage(layer, 0, 0);
      ctx.restore();
    };

    const applyFadingCanvasBlur = (
      ctx: CanvasRenderingContext2D,
      width: number,
      height: number,
      blurAmount: number
    ) => {
      const snapshot = document.createElement('canvas');
      snapshot.width = width;
      snapshot.height = height;

      const snapshotCtx = snapshot.getContext('2d');
      if (!snapshotCtx) return;

      snapshotCtx.drawImage(ctx.canvas, 0, 0);

      const blurred = document.createElement('canvas');
      blurred.width = width;
      blurred.height = height;

      const blurredCtx = blurred.getContext('2d');
      if (!blurredCtx) return;

      blurredCtx.save();
      blurredCtx.filter = `blur(${blurAmount}px)`;
      blurredCtx.drawImage(snapshot, 0, 0);
      blurredCtx.restore();

      /**
       * Fade the blur out toward the top.
       * Top = no blur
       * Lower area = full blur
       */
      const fade = blurredCtx.createLinearGradient(0, 0, 0, height);
      fade.addColorStop(0, 'rgba(0,0,0,0)');
      fade.addColorStop(0.16, 'rgba(0,0,0,0)');
      fade.addColorStop(0.38, 'rgba(0,0,0,0.45)');
      fade.addColorStop(0.62, 'rgba(0,0,0,1)');
      fade.addColorStop(1, 'rgba(0,0,0,1)');

      blurredCtx.save();
      blurredCtx.globalCompositeOperation = 'destination-in';
      blurredCtx.fillStyle = fade;
      blurredCtx.fillRect(0, 0, width, height);
      blurredCtx.restore();

      /**
       * Draw blurred copy on top of the original.
       * This gives the whole glow a soft final blur without creating a hard top line.
       */
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      ctx.drawImage(blurred, 0, 0);
      ctx.restore();
    };

    const drawGrain = (
      ctx: CanvasRenderingContext2D,
      width: number,
      height: number
    ) => {
      let grain = grainCanvasRef.current;

      const needsNewGrain =
        !grain || grain.width !== width || grain.height !== height;

      if (needsNewGrain) {
        grain = document.createElement('canvas');
        grain.width = width;
        grain.height = height;
        grainCanvasRef.current = grain;

        const grainCtx = grain.getContext('2d');
        if (!grainCtx) return;

        grainCtx.clearRect(0, 0, width, height);

        const dpr = window.devicePixelRatio || 1;
        const baseSize = 0.25 * dpr;
        const density = 0.03; // Further increased density for "even more grain" and "more coarse"
        const count = Math.floor(
          (width * height * density) / (baseSize * baseSize)
        );

        for (let i = 0; i < count; i++) {
          const x = Math.random() * width;
          const y = Math.random() * height;

          // Mostly 1x1, some 2x1, some 1x2, rarely 2x2 blocks
          const r = Math.random();
          let wMult = 1;
          let hMult = 1;

          if (r > 0.9) {
            wMult = 2;
            hMult = 2;
          } else if (r > 0.8) {
            wMult = 2;
          } else if (r > 0.7) {
            hMult = 2;
          }

          const w = baseSize * wMult;
          const h = baseSize * hMult;

          const opacity = 0.15 + Math.random() * 0.4;
          grainCtx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
          grainCtx.fillRect(x, y, w, h);
        }
      }

      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      ctx.drawImage(grain as HTMLCanvasElement, 0, 0);
      ctx.restore();
    };

    const draw = (ts: number) => {
      if (!archCanvasRef.current || !glowsCanvasRef.current) return;

      const archCanvas = archCanvasRef.current;
      const glowsCanvas = glowsCanvasRef.current;

      const archCtx = archCanvas.getContext('2d');
      const glowsCtx = glowsCanvas.getContext('2d');

      if (!archCtx || !glowsCtx) return;

      const now = performance.now();
      const dt = clamp((now - lastTimeRef.current) / 1000, 0, 0.1);
      lastTimeRef.current = now;

      const targetInterp = stateRef.current === 'speaking' ? 1 : 0;
      const lerpSpeed = 4.5;

      interpolationRef.current = clamp(
        interpolationRef.current +
          (targetInterp - interpolationRef.current) *
            Math.min(1, dt * lerpSpeed),
        0,
        1
      );

      const interp = interpolationRef.current;
      const w = archCanvas.width;
      const h = archCanvas.height;

      archCtx.clearRect(0, 0, w, h);
      glowsCtx.clearRect(0, 0, w, h);

      if (clipToRadiusRef.current) {
        [archCtx, glowsCtx].forEach((ctx) => {
          ctx.save();
          roundedRectPath(ctx, 0, 0, w, h, radiusRef.current * dpr);
          ctx.clip();
        });
      }

      const leftColor = leftColorRef.current;
      const rightColor = rightColorRef.current;

      const opacity = opacityRef.current;
      const blur = blurRef.current;

      const analyser = analyserRef.current;
      const currentLevel = analyser
        ? analyser.getAmplitude()
        : levelRef.current;

      const baseLvl = clamp(currentLevel, 0, 1);
      const minLvl = 0.06 * interp;
      const lvl = Math.max(minLvl, baseLvl);

      const barCenterY = h * 0.5;

      const drawMainArch = () => {
        archCtx.save();

        const speakingAlpha = 1 + lvl * 0.15 * interp;
        const speakingBlurScale = 1 + lvl * 0.08 * interp;
        const peakBoost = lvl * 0.035 * interp;

        const points = getArchPoints(barCenterY, h, peakBoost);

        /**
         * Wider gradient:
         * - blue starts earlier and stretches farther left
         * - orange starts earlier and stretches farther right
         * - purple/magenta bridges the center
         */

        const archGradient = archCtx.createLinearGradient(
          -w * 0.35,
          h * 0.34,
          w * 1.35,
          h
        );

        archGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        // archGradient.addColorStop(
        //   0.04,
        //   withAlpha(ambientColor, opacity * 0.65)
        // );
        archGradient.addColorStop(0.16, withAlpha(leftColor, opacity));
        archGradient.addColorStop(0.34, withAlpha(leftColor, opacity * 0.95));
        archGradient.addColorStop(0.48, withAlpha(leftColor, opacity));
        archGradient.addColorStop(0.56, withAlpha(rightColor, opacity * 1));
        archGradient.addColorStop(0.84, withAlpha(rightColor, opacity));
        archGradient.addColorStop(0.96, withAlpha(rightColor, opacity * 0.5));
        archGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

        // Wide atmospheric color. This makes the color reach the sides.
        drawSoftMaskedGradient(archCtx, points, w, h, archGradient, {
          maskBlur: Math.max(95 * dpr, blur * 2.2 * dpr) * speakingBlurScale,
          yOffset: -12 * dpr,
          alpha: opacity * 1.25 * speakingAlpha,
          topFadeStart: 0.0,
          topFadeEnd: 0.78,
        });
        // Main visible body.
        drawSoftMaskedGradient(archCtx, points, w, h, archGradient, {
          maskBlur: Math.max(62 * dpr, blur * 1.55 * dpr) * speakingBlurScale,
          yOffset: 18 * dpr,
          alpha: opacity * 1.15 * speakingAlpha,
          topFadeStart: 0.06,
          topFadeEnd: 0.88,
        });

        // Lower saturated color band.
        drawSoftMaskedGradient(archCtx, points, w, h, archGradient, {
          maskBlur: Math.max(38 * dpr, blur * 1.05 * dpr) * speakingBlurScale,
          yOffset: 92 * dpr,
          alpha: opacity * 0.95 * speakingAlpha,
          topFadeStart: 0.22,
          topFadeEnd: 1,
        });

        // Extra low color wash that stretches across the full width.
        const lowWash = archCtx.createLinearGradient(
          -w * 0.1,
          h * 0.62,
          w * 1.1,
          h
        );
        lowWash.addColorStop(0, withAlpha(leftColor, opacity * 0.35));
        lowWash.addColorStop(0.22, withAlpha(leftColor, opacity * 0.78));
        lowWash.addColorStop(0.78, withAlpha(rightColor, opacity * 0.82));
        lowWash.addColorStop(1, withAlpha(rightColor, opacity * 0.38));

        drawSoftMaskedGradient(archCtx, points, w, h, lowWash, {
          maskBlur: Math.max(58 * dpr, blur * 1.25 * dpr),
          yOffset: 128 * dpr,
          alpha: opacity * 0.9,
          topFadeStart: 0.34,
          topFadeEnd: 1,
        });

        /**
         * Very light edge eraser.
         * Lower opacity/width keeps colors prominent while still softening the top boundary.
         */
        archCtx.save();
        archCtx.globalCompositeOperation = 'destination-out';
        archCtx.filter = `blur(${Math.max(22 * dpr, blur * 0.55 * dpr)}px)`;

        archCtx.beginPath();
        archCtx.moveTo(points[0][0] * w, points[0][1] * h);

        for (let i = 1; i < points.length - 1; i++) {
          const [cx, cy] = points[i];
          const [nx, ny] = points[i + 1];

          archCtx.quadraticCurveTo(
            cx * w,
            cy * h,
            ((cx + nx) / 2) * w,
            ((cy + ny) / 2) * h
          );
        }

        archCtx.strokeStyle = 'rgba(0,0,0,0.06)';
        archCtx.lineWidth = 18 * dpr;
        archCtx.stroke();
        archCtx.restore();

        drawGrain(archCtx, w, h);

        // Final whole-canvas blur pass.

        // This makes the entire glow softer, while fading the blur out toward the top.

        applyFadingCanvasBlur(
          archCtx,

          w,

          h,

          Math.max(18 * dpr, blur * 0.22 * dpr)
        );

        archCtx.restore();
      };

      drawMainArch();

      /**
       * Old radial blob glows intentionally disabled.
       * The playground look is created by the masked arch only.
       */

      if (clipToRadiusRef.current) {
        [archCtx, glowsCtx].forEach((ctx) => ctx.restore());
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, []);

  const commonCanvasStyles: React.CSSProperties = {
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: -1,
  };

  return (
    <div
      className={`caddietw-absolute caddietw-inset-0 caddietw-pointer-events-none ${
        className ?? ''
      }`}
      style={style}
    >
      <canvas
        ref={archCanvasRef}
        aria-hidden
        style={{
          ...commonCanvasStyles,
          zIndex: 0,
          backdropFilter: backdropBlur ? `blur(${backdropBlur}px)` : undefined,
          WebkitBackdropFilter: backdropBlur
            ? `blur(${backdropBlur}px)`
            : undefined,
          maskImage: `
            linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)
          `,
          WebkitMaskImage: `
            linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)
          `,
          maskComposite: 'intersect',
          WebkitMaskComposite: 'source-in',
        }}
      />
      <canvas
        ref={glowsCanvasRef}
        aria-hidden
        style={{
          ...commonCanvasStyles,
          zIndex: -1,
          opacity,
        }}
      />

      {!shouldHidePoweredBy && (
        <div
          className={cn(
            'caddietw-absolute caddietw-bottom-[-28px] caddietw-left-1/2 -caddietw-translate-x-1/2',
            'caddietw-flex caddietw-items-center caddietw-gap-1.5 caddietw-cursor-pointer caddietw-pointer-events-auto',
            'caddietw-select-none hover:caddietw-opacity-80 caddietw-transition-opacity caddietw-group'
          )}
          style={{
            color: poweredByColor,
            fontFamily: 'Inter',
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '18px',
            zIndex: 1,
          }}
          onClick={handlePoweredBySuperbarClick}
        >
          <span
            className="caddietw-whitespace-nowrap caddietw-flex caddietw-items-center caddietw-gap-1"
            style={{ color: poweredByColor }}
          >
            Powered by
            <span
              style={{
                fontWeight: '900',
              }}
            >
              Superbar
            </span>
          </span>
          <SuperbarDark
            className="caddietw-w-4 caddietw-h-4 caddietw-transition-all"
            stroke="currentColor"
            logoColor={logoColor}
          />
        </div>
      )}
    </div>
  );
};

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

function withAlpha(color: string, alpha: number) {
  const safeAlpha = Math.max(0, Math.min(1, alpha));

  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const normalized =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;

    const bigint = parseInt(normalized, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
  }

  return color;
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const rr = Math.min(r, w / 2, h / 2);

  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export default GlowBackground;
